import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useCSATByPage } from '@/hooks/useCSATByPage';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface CSATByPageTableProps {
  daysRange: number;
}

const getRatingColor = (rating: number): string => {
  if (rating >= 4.5) return 'text-green-600';
  if (rating >= 4.0) return 'text-green-500';
  if (rating >= 3.5) return 'text-yellow-500';
  if (rating >= 3.0) return 'text-orange-500';
  return 'text-red-500';
};

const getCSATColor = (csat: number): string => {
  if (csat >= 80) return 'text-green-600';
  if (csat >= 60) return 'text-green-500';
  if (csat >= 40) return 'text-yellow-500';
  if (csat >= 20) return 'text-orange-500';
  return 'text-red-500';
};

export const CSATByPageTable = ({ daysRange }: CSATByPageTableProps) => {
  const { data, isLoading } = useCSATByPage(daysRange);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>CSAT por Página</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[200px] flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>CSAT por Página</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[200px] flex items-center justify-center text-muted-foreground">
            <p>Nenhum dado disponível</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>CSAT por Página</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Página</TableHead>
                <TableHead className="text-right">CSAT Médio</TableHead>
                <TableHead className="text-right">CSAT Score</TableHead>
                <TableHead className="text-right">Nº Respostas</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((page) => (
                <TableRow key={page.page_id}>
                  <TableCell className="font-medium">
                    {page.page_name}
                  </TableCell>
                  <TableCell className={`text-right font-semibold ${getRatingColor(page.averageRating)}`}>
                    {page.averageRating.toFixed(1)}/5.0
                  </TableCell>
                  <TableCell className={`text-right font-semibold ${getCSATColor(page.csatScore)}`}>
                    {page.csatScore}%
                  </TableCell>
                  <TableCell className="text-right text-muted-foreground">
                    {page.totalResponses}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="mt-4 text-xs text-muted-foreground">
          <p>
            <strong>CSAT Médio:</strong> Média aritmética de todas as avaliações (1-5 estrelas)
          </p>
          <p className="mt-1">
            <strong>CSAT Score:</strong> Percentual de respostas com avaliação ≥ 4 estrelas
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

